import React, {useEffect, useState} from 'react';
import pick from 'lodash/pick';
import {fetchUserData, login, socialLogin, updateActivity, updateUser, registerPushNotificationInteraction} from '../endpoints/api';
import {PENDING_INTERACTIONS_KEY} from './useNotificationInteractionTracking';
import camelize from '../utils/camelize';
import {useNavigate} from 'react-router-dom';
import {deleteFromStorage, useLocalStorage} from '@rehooks/local-storage';
import {EventRSVPStatus} from './useEvent';
import {fetchToken} from '../firebase';
import {deviceDetected, isIphone} from '../utils/deviceCheck';
import {Community} from '../types/community';
import * as pushService from '../pushService';
import {ActiveMembership} from '../types/membership';
import {queryClient} from '../App';
import {setAnalyticsUserId, clearUserAnalytics, initializeUserAnalytics} from '../utils/analytics';

export type SuperFilters = {
  id: number;
  user_id: number;
  topic_ids: string[];
  lifestyle_group_ids: string[];
  is_deleted: number;
};

export interface User {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone: string;
  pointsTotal: number;
  mailingAddress: string;
  aboutMe: string;
  lifestyleGroups: Record<string, string>[];
  topics: Record<string, string>[];
  smsEnabled: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;
  deviceToken?: string | null;
  profilePhotoUrl: string;
  community: Record<string, string>;
  referralCode: string;
  isFenton: boolean;
  superfilters: SuperFilters;
  hasEarlyAccess: boolean;
  handle: string;
  atm_rsvp_status?: EventRSVPStatus;
  communities?: Community[];
  isWalkaboutEmployee?: boolean;
  pushEnabledUpdatedAt?: string;
  isMobileDebugEnabled?: boolean;
  isMobileDebugAvailable?: boolean;
  createdAt?: string;
  hasActiveMembership?: boolean;
  activeMemberships?: ActiveMembership[];
  activeMembershipPlan?: any;
  activeMembershipPlanSubscription?: any;
  membershipActivities?: any;
  roles?: any;
  isInternal?: boolean;
  deviceTokenRefreshRequired?: boolean;
}

/**
 * This is the API provided by useAuth()
 */
interface AuthAPI {
  user?: User;
  isUserAuthenticated: boolean;
  reFetchUserData: () => Promise<void>;
  patchUser: (partial: Partial<User>) => void;
  // returns the default page url for the newly logged in user
  login(email: string, password: string): Promise<string | void>;
  socialLogin(provider: string, token:string): Promise<string | void>;
  loginWithToken(token: string): void;
  logout(): void;
}

const AuthContext = React.createContext<AuthAPI>({} as AuthAPI);

export const USER_TOKEN = 'user_token';
export const USER_DELETED_AT = 'user_deleted_at';

const DEFAULT_PAGE = '/home';

export const AuthProvider = ({children}) => {
  const [user, setUser] = useState<User>();
  const [userToken, setUserToken] = useLocalStorage<string>(USER_TOKEN);
  const [userDeletedAt, setUserDeletedAt] = useLocalStorage<string | null>(USER_DELETED_AT);
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const navigate = useNavigate();

  const setDeviceToken = async () => {
    const device_token = await fetchToken();
    if (device_token)
      await updateUser({device_token: device_token, device_platform: deviceDetected()});
  };

  // useEffect(() => {
  //   if (userToken) {
  //     console.log('setting device token');
  //     console.log(userToken);
  //     setDeviceToken().catch(console.error);
  //   }
  // }, [userToken]);

  const drainPendingInteractions = () => {
    const pending: Array<{message_uuid: string; source: 'foreground' | 'background' | 'killed' | 'web'; occurred_at: string}> =
      JSON.parse(sessionStorage.getItem(PENDING_INTERACTIONS_KEY) || '[]');
    if (pending.length === 0) return;
    pending.forEach((item) => {
      registerPushNotificationInteraction({
        message_uuid: item.message_uuid,
        interaction_type: 'clicked',
        source: item.source || 'background',
        occurred_at: item.occurred_at,
      }).catch(() => {});
    });
    sessionStorage.removeItem(PENDING_INTERACTIONS_KEY);
  };

  const loginHandler = async (email: string, password: string) => {
    const {data: token, status} = await login({
      email,
      password,
    });
    if (status === 200) {
      // This causes a 401 (unauthicated) error on dev because the updateUser call happens before the user token is actually set.
      // Moved to useEffect instead to fire only when userToken is actually set.
      // const device_token = await fetchToken();
      // if (device_token) await updateUser({device_token: device_token, device_platform: deviceDetected()});
      setUserToken(token);
      drainPendingInteractions();
      return DEFAULT_PAGE;
    }
  };

  const socialLoginHandler = async (provider: string, idToken: string) => {
    const {data, status} = await socialLogin({
      provider,
      idToken,
    });
    if (status === 200) {
      const {token, isNewUser} = camelize(data);
      setUserToken(token);
      drainPendingInteractions();
      return isNewUser ? '/onboarding' : DEFAULT_PAGE;
    }
  };

  const loginWithTokenHandler = (token) => {
    if (!token) throw new Error('Invalid token.');
    setUserToken(token);
    drainPendingInteractions();
  };


  const logoutHandler = async () => {
    deleteFromStorage(USER_TOKEN);
    // this is the user object that is stored locally for auth-sessions. On logout it needs to be cleared -> undefined/null
    setUser(undefined);
    
    // Clear analytics user ID and stored user data on logout
    clearUserAnalytics();
    
    // Reset analytics initialization flag for next login
    setAnalyticsInitialized(false);
    
    const message = {
      type: 'logout',
    };
    if (window?.['ReactNativeWebView']) {
      window['ReactNativeWebView'].postMessage(JSON.stringify(message));
    }
    queryClient.clear();
    navigate('/auth');
  };

  const loadUserProfile = () => {
    return fetchUserData()
      .then(({status, data}) => {
        if (status === 200 && data.deleted_at === null) {
          setUserFromPayload(data);
          setUserDeletedAt(data.deleted_at);
        } else {
          console.error(status, data);
          logoutHandler();
        }
      })
      .catch(() => {
        deleteFromStorage(USER_TOKEN);
        setUser(undefined);
        clearUserAnalytics();
      });
  };

  const setUserFromPayload = (payload) => {
    const p = camelize(payload);
    const newUser = {
      ...pick(p, [
        'id',
        'name',
        'surname',
        'email',
        'phone',
        'email',
        'pointsTotal',
        'aboutMe',
        'lifestyleGroups',
        'topics',
        'smsEnabled',
        'pushEnabled',
        'emailEnabled',
        'deviceToken',
        'profilePhotoUrl',
        'community',
        'mailingAddress',
        'referredBy',
        'referralCode',
        'isFenton',
        'superfilters',
        'hasEarlyAccess',
        'handle',
        'communities',
        'isWalkaboutEmployee',
        'pushEnabledUpdatedAt',
        'isMobileDebugEnabled',
        'isMobileDebugAvailable',
        'createdAt',
        'hasActiveMembership',
        'activeMemberships',
        'activeMembershipPlan',
        'activeMembershipPlanSubscription',
        'membershipActivities',
        'roles',
        'isInternal',
        'deviceTokenRefreshRequired',
      ]),
    };
    if (window?.['ReactNativeWebView']) {
      window?.['ReactNativeWebView']?.postMessage(JSON.stringify({type: 'user', user: newUser}));
    }
    
    setUser(newUser);
    
    // Note: Automatic redirection removed - now handled by onboarding flow
    // redirectIfMissingData(newUser);
  };

  useEffect(() => {
    if (userToken && !user) {
      loadUserProfile();
    }
  }, [userToken, user]);

  // Initialize analytics once per session when we have complete user data
  useEffect(() => {
    if (user?.id && !analyticsInitialized) {
      // Set the flag immediately to prevent race conditions
      setAnalyticsInitialized(true);
      
      // Don't await this to avoid blocking the UI
      initializeUserAnalytics(user)
        .catch(error => {
          console.warn('[Analytics] Failed to initialize user analytics, resetting flag:', error);
          // Reset flag on error so it can be retried
          setAnalyticsInitialized(false);
        });
    }
  }, [user, analyticsInitialized]);

  useEffect(() => {
    let interval = setInterval(() => {
      if (user && document.visibilityState === 'visible') updateActivity();
    }, 120000);

    const switched = () => {
      if (user && document.visibilityState === 'visible') {
        interval = setInterval(() => {
          updateActivity();
        }, 120000);
      } else clearInterval(interval);

      setTimeout(() => {
        if (user && document.visibilityState === 'hidden') {
          updateActivity();
        }
      }, 0);
    };
    const closed = () => {
      if (user) updateActivity();
    };

    // tab switched
    document.addEventListener('visibilitychange', switched);

    // app closed
    window.addEventListener('unload', closed);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', switched);
      window.removeEventListener('unload', closed);
    };
  }, [user]);

  useEffect(() => {
    if (window['ReactNativeWebView'] && user) {
      const message = {
        type: 'user',
        user,
      };
      window['ReactNativeWebView'].postMessage(JSON.stringify(message));
      
      // Retroactive fix: If user has push_enabled but no device_token,
      // re-request permission to get a valid token
      if (user.pushEnabled && !user.deviceToken) {
        console.log('[Push] Retroactive fix: User has pushEnabled but no deviceToken, re-requesting permission');
        pushService.pushPermissionRequest();
      }
    }
  }, [window['ReactNativeWebView'], user]);

  // Retroactive fix for web users (non-React Native, non-iOS)
  // If user has push_enabled but no device_token, request permission to get a valid token
  useEffect(() => {
    const fixMissingDeviceToken = async () => {
      if (!window['ReactNativeWebView'] && !isIphone && user?.pushEnabled && !user?.deviceToken) {
        console.log('[Push] Retroactive fix: Web user has pushEnabled but no deviceToken, requesting permission');
        try {
          const token = await pushService.askNotificationPermission();
          if (token) {
            await updateUser({
              device_token: token,
              device_platform: deviceDetected(),
              push_enabled: true,
            });
            console.log('[Push] Retroactive fix: Successfully obtained and saved device token');
          }
        } catch (error) {
          console.error('[Push] Retroactive fix: Failed to get push notification token:', error);
          // If we can't get a token, disable push to fix the inconsistent state
          await updateUser({ push_enabled: false });
        }
      }
    };
    
    if (user) {
      fixMissingDeviceToken();
    }
  }, [user?.id, user?.pushEnabled, user?.deviceToken]);

  useEffect(() => {
    if (window['ReactNativeWebView'] && userToken) {
      const message = {
        type: 'token',
        token: userToken,
      };
      window['ReactNativeWebView'].postMessage(JSON.stringify(message));
    }
  }, [window['ReactNativeWebView'], userToken]);

  const auth: AuthAPI = {
    user,
    isUserAuthenticated: !!userToken,
    reFetchUserData: loadUserProfile,
    patchUser: (partial) => {
      setUser((prev) => (prev ? {...prev, ...partial} as User : prev));
    },
    login: loginHandler,
    socialLogin: socialLoginHandler,
    loginWithToken: loginWithTokenHandler,
    logout: logoutHandler,
  };

  // Automatic redirection removed - now handled by new onboarding flow
  // const redirectIfMissingData = (user) => {
  //   const lifestyleGoalRoute = '/register/goals-interests';
  //   const topicsRoute = '/register/goals-interests';
  //   const usernameRoute = '/register/username';

  //   if ([lifestyleGoalRoute, topicsRoute, usernameRoute].includes(window.location.pathname)) return;

  //   if (
  //     !window.location.pathname.startsWith('/profile') &&
  //     !window.location.pathname.includes('explainer') &&
  //     !window.location.pathname.includes('/profile-pic') &&
  //     !window.location.pathname.includes('register') &&
  //     !window.location.pathname.includes('home')
  //   ) {
  //     if (!user.handle || user.handle.length < 1) {
  //       return navigate(usernameRoute);
  //     }

  //     if (user.lifestyleGroups.length < 1) {
  //       return navigate(lifestyleGoalRoute);
  //     }

  //     if (user.topics.length < 1) {
  //       return navigate(topicsRoute);
  //     }
  //   }
  // };

  const loadingProfile = userToken && !user;

  return (
    <AuthContext.Provider value={auth}>{loadingProfile ? null : children}</AuthContext.Provider>
  );
};

export default function useAuth() {
  return React.useContext(AuthContext);
}
